import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { CheckCircle2, ShieldCheck, Calendar, Headphones, ShieldAlert, Sparkles } from "lucide-react";
import VoltIcon from "./VoltIcon";

/**
 * BillingActivatedBanner — Reliant/Rhythm-tier welcome card.
 *
 * Layout:
 *   • Desktop: image LEFT, copy RIGHT (50/50)
 *   • Mobile : image TOP, copy BOTTOM (flex-col-reverse on md+, flex-col on
 *              mobile so image is on top — no order/jump glitches).
 */
const FOOT_FEATURES = [
  { icon: VoltIcon, key: "foot1" },
  { icon: Headphones, key: "foot2" },
  { icon: ShieldAlert, key: "foot3" },
];

export default function BillingActivatedBanner() {
  const { t } = useTranslation();

  return (
    <section className="relative bg-white" data-testid="billing-activated-banner">
      <div className="max-w-[1180px] mx-auto px-5 py-16 md:py-24">
        <div className="flex flex-col md:grid md:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] md:gap-12 lg:gap-16 items-stretch gap-10">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full md:h-full"
          >
            <div className="relative overflow-hidden rounded-[28px] aspect-[4/5] md:aspect-auto md:h-full md:min-h-[480px] shadow-[0_28px_60px_rgba(15,23,42,0.12)] group w-full">
              <img
                src="/family-billing.png"
                alt={t("home.billingImageAlt")}
                className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
                loading="lazy"
                data-testid="billing-banner-image"
              />
              <div className="absolute inset-0 ring-1 ring-black/5 rounded-[28px] pointer-events-none" />
            </div>
            {/* Floating "Servicio activo" badge */}
            <div className="absolute bottom-4 right-4 md:bottom-5 md:right-5 inline-flex items-center gap-1.5 bg-velcor-green text-white rounded-full pl-2 pr-3.5 py-1.5 shadow-[0_14px_28px_rgba(47,122,58,0.40)]">
              <span className="grid place-items-center w-5 h-5 rounded-full bg-white/20">
                <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.6} />
              </span>
              <span className="font-extrabold tracking-tight" style={{ fontSize: 12 }}>
                {t("home.billingBadge")}
              </span>
            </div>
          </motion.div>

          {/* Copy */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
            className="w-full flex flex-col justify-center"
          >
            {/* Kicker */}
            <span
              className="inline-flex items-center gap-1.5 rounded-full bg-velcor-green/10 px-3 py-1.5 text-velcor-green mb-5 self-start"
              style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.16em" }}
            >
              <Sparkles className="w-3 h-3" strokeWidth={2.4} />
              {t("home.billingKicker")}
            </span>

            <h2
              className="display text-velcor-ink leading-[1.06]"
              style={{ fontSize: "clamp(26px, 4vw, 44px)", fontWeight: 600, letterSpacing: "-0.02em" }}
              data-testid="billing-banner-title"
            >
              {t("home.billingTitle")}{" "}
              <span className="text-velcor-green">{t("home.billingTitleAccent")}</span>
            </h2>

            <p className="mt-5 text-slate-600 leading-relaxed" style={{ fontSize: 15, fontWeight: 400 }}>
              {t("home.billingP1")}
            </p>
            <p className="mt-3 text-slate-600 leading-relaxed" style={{ fontSize: 15, fontWeight: 400 }}>
              {t("home.billingP2")}
            </p>

            {/* Pago Diferido — soft card */}
            <div
              className="mt-7 rounded-2xl bg-velcor-green/5 border border-velcor-green/20 p-5 flex items-start gap-3.5"
              data-testid="billing-deferred"
            >
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-velcor-green text-white shrink-0 shadow-[0_10px_22px_rgba(47,122,58,0.30)]">
                <Calendar className="w-5 h-5" strokeWidth={2.2} />
              </span>
              <div className="min-w-0">
                <h3 className="text-velcor-ink tracking-tight" style={{ fontSize: 16, fontWeight: 700 }}>
                  {t("home.deferredTitle")}
                </h3>
                <p className="mt-1.5 text-slate-600 leading-relaxed" style={{ fontSize: 13.5, fontWeight: 400 }}>
                  {t("home.deferredBody")}
                </p>
              </div>
            </div>

            {/* Reassurance line */}
            <div className="mt-5 flex items-start gap-2.5">
              <ShieldCheck className="w-5 h-5 text-velcor-green mt-0.5 shrink-0" strokeWidth={2.2} />
              <p className="text-slate-700 leading-relaxed" style={{ fontSize: 14, fontWeight: 500 }}>
                {t("home.deferredFooter")}
              </p>
            </div>

            {/* Three small bottom features */}
            <div className="mt-7 grid grid-cols-3 gap-3 md:gap-4 pt-6 border-t border-slate-100" data-testid="billing-foot">
              {FOOT_FEATURES.map((f) => (
                <div key={f.key} className="flex items-start gap-2.5">
                  <span className="grid place-items-center w-9 h-9 rounded-full bg-velcor-green/10 text-velcor-green shrink-0">
                    <f.icon className="w-3.5 h-3.5" strokeWidth={2.4} />
                  </span>
                  <div className="min-w-0">
                    <div className="text-velcor-ink tracking-tight leading-tight" style={{ fontSize: 13, fontWeight: 700 }}>
                      {t(`home.billing_${f.key}_title`)}
                    </div>
                    <div className="mt-0.5 text-slate-500 leading-snug" style={{ fontSize: 11.5, fontWeight: 400 }}>
                      {t(`home.billing_${f.key}_body`)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
